/**
 * FinsaDocumentationPanel — TASK-704.
 *
 * The FINSA documentation a meeting actually produced, read back from the record rather than
 * regenerated. `write_finsa_notes` ran for every meeting-bound dictation and its output was
 * returned to the browser and dropped — `meeting_documentation` held zero rows account-wide.
 *
 * Three rules this component exists to keep:
 *
 *  - the dictated source is shown UNDER its provenance label, never as a transcript;
 *  - the typed regulatory booleans are reported as **not confirmed**, because their only
 *    candidate source is a hardcoded `compliance_validated: True` that is returned even for an
 *    empty transcript. A green tick here would be an attestation nobody made;
 *  - "there is no documentation" and "we could not load it" render differently. An empty
 *    surface read as an absence of work is the failure this whole epic exists to fix.
 */
import { useEffect, useState } from 'react';
import { FileText, ShieldAlert, AlertTriangle } from 'lucide-react';

import { postMeetingApi } from '../../../services/api/postMeetingApi';

interface SessionDocumentation {
  source_kind?: string | null;
  source_text?: string | null;
  provenance_label?: string | null;
  full_documentation?: string | null;
  executive_summary?: string | null;
  key_insights?: Record<string, unknown> | null;
  suitability_confirmed?: boolean;
  client_consent_obtained?: boolean;
  financial_situation_reviewed?: boolean;
  investment_objectives_verified?: boolean;
  risk_tolerance_assessed?: boolean;
  /**
   * TASK-930: the versions this record corrected, newest first. A second dictation supersedes
   * rather than overwrites, and a corrected regulatory record must still show what was
   * originally approved — showing only the newest would discard the evidence the retention
   * exists for.
   */
  superseded_versions?: Array<{
    source_note_id?: string | null;
    provenance_label?: string | null;
    source_text?: string | null;
    full_documentation?: string | null;
    created_at?: string | null;
    created_by?: string | null;
  }> | null;
}

type Load =
  | { state: 'loading' }
  | { state: 'absent' }
  | { state: 'unavailable' }
  | { state: 'ready'; doc: SessionDocumentation };

/** The typed FinSA checks, in the order a reviewer reads them. */
const CHECKS: [keyof SessionDocumentation, string][] = [
  ['suitability_confirmed', 'Suitability'],
  ['client_consent_obtained', 'Client consent'],
  ['financial_situation_reviewed', 'Financial situation reviewed'],
  ['investment_objectives_verified', 'Investment objectives verified'],
  ['risk_tolerance_assessed', 'Risk tolerance assessed'],
];

export function FinsaDocumentationPanel({ sessionId }: { sessionId: string }) {
  const [load, setLoad] = useState<Load>({ state: 'loading' });

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    (async () => {
      try {
        const data = await postMeetingApi.getPostMeetingData(sessionId);
        if (!alive) return;
        if (data.documentation) {
          setLoad({ state: 'ready', doc: data.documentation as SessionDocumentation });
        } else {
          // The two negatives are NOT the same answer, and only one is a clean slate.
          setLoad({ state: data.documentation_unavailable ? 'unavailable' : 'absent' });
        }
      } catch {
        // A failed lookup is "we could not check", never "there is nothing".
        if (alive) setLoad({ state: 'unavailable' });
      }
    })();
    return () => {
      alive = false;
    };
  }, [sessionId]);

  if (!sessionId || load.state === 'loading') {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {sessionId ? 'Loading FINSA documentation…' : 'No meeting selected.'}
      </p>
    );
  }

  if (load.state === 'unavailable') {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-600" aria-hidden="true" />
        <p className="text-sm text-amber-900">
          The FINSA documentation could not be loaded. This is not the same as the meeting
          having none — try again before concluding anything from it.
        </p>
      </div>
    );
  }

  if (load.state === 'absent') {
    return (
      <p className="text-sm text-muted-foreground">
        No FINSA documentation has been recorded for this meeting.
      </p>
    );
  }

  const { doc } = load;
  const insights = Object.entries(doc.key_insights ?? {}).filter(
    ([, v]) => Array.isArray(v) && v.length > 0
  ) as [string, string[]][];

  return (
    <section className="space-y-4" aria-label="FINSA documentation">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <FileText className="h-4 w-4" aria-hidden="true" />
        FINSA documentation
      </h3>

      {doc.full_documentation && (
        <p className="whitespace-pre-wrap text-sm text-foreground">{doc.full_documentation}</p>
      )}

      {insights.length > 0 && (
        <dl className="space-y-2">
          {insights.map(([key, values]) => (
            <div key={key}>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {key.replace(/_/g, ' ')}
              </dt>
              <dd className="text-sm text-foreground">
                <ul className="list-disc pl-5">
                  {values.map((v, i) => (
                    <li key={i}>{v}</li>
                  ))}
                </ul>
              </dd>
            </div>
          ))}
        </dl>
      )}

      {doc.source_text && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          {/* The label first, then the words — so the caveat cannot be missed. */}
          {doc.provenance_label && (
            <p className="mb-2 text-xs font-medium text-amber-800">{doc.provenance_label}</p>
          )}
          <p className="whitespace-pre-wrap text-sm text-foreground">{doc.source_text}</p>
        </div>
      )}

      <div className="rounded-lg border border-border p-3">
        <p className="mb-2 flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" />
          Regulatory checks — recorded from this meeting only
        </p>
        <ul className="space-y-1">
          {CHECKS.map(([key, label]) => (
            <li key={key} className="flex items-center justify-between text-sm">
              <span className="text-foreground">{label}</span>
              <span className={doc[key] ? 'text-green-700' : 'text-muted-foreground'}>
                {doc[key] ? 'Confirmed' : 'Not confirmed'}
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-muted-foreground">
          These are not set by generation. Documenting a meeting does not attest to any of
          them — an adviser records them explicitly.
        </p>
      </div>

      {/* TASK-930: earlier versions, newest first. Collapsed, because the CURRENT record is
          what a reviewer reads — but present, because "this was corrected" is itself a fact
          about a regulatory record, and the prior wording is what an auditor would ask for. */}
      {(doc.superseded_versions?.length ?? 0) > 0 && (
        <details className="rounded-lg border border-amber-200 bg-amber-50/60 p-3">
          <summary className="cursor-pointer text-xs font-medium text-amber-900">
            Corrected — {doc.superseded_versions!.length} earlier version
            {doc.superseded_versions!.length === 1 ? '' : 's'} retained
          </summary>
          <ol className="mt-3 space-y-3">
            {doc.superseded_versions!.map((prior, i) => (
              <li
                key={prior.source_note_id || i}
                className="rounded-md border border-border bg-background p-3"
              >
                <p className="mb-1 text-xs text-muted-foreground">
                  {prior.created_at
                    ? new Date(prior.created_at).toLocaleString()
                    : 'date unknown'}
                  {prior.created_by ? ` · ${prior.created_by}` : ''}
                </p>
                {prior.provenance_label && (
                  <p className="mb-1 text-xs font-medium text-amber-800">
                    {prior.provenance_label}
                  </p>
                )}
                <p className="whitespace-pre-wrap text-sm text-foreground">
                  {prior.source_text || prior.full_documentation || '—'}
                </p>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

export default FinsaDocumentationPanel;
